"use client";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X, Loader2, CheckCircle } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useOrganizationStore } from "@/stores/organization";
import { useState } from "react";
import { toast } from "sonner";
import { fetchJson } from "@/services/api-client";
import { showApiErrorToast } from "@/lib/api-error-toast";

const PITCH_DECK_URL = "https://pitch.com/v/early-adopter-program-glaci-nncqrn";

interface EarlyAdopterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EarlyAdopterDialog({
  open,
  onOpenChange,
}: EarlyAdopterDialogProps) {
  const organization = useOrganizationStore((state) => state.organization);
  const setOrganization = useOrganizationStore(
    (state) => state.setOrganization,
  );
  const organizationName = organization?.name || "Your Organization";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Check if already requested from persisted state
  const hasAlreadyRequested = organization?.hasRequestedGlaciAccess ?? false;

  const handleRequestAccess = async () => {
    if (!organization?.id) {
      toast.error("Unable to submit request", {
        description: "Organization not found. Please try again.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await fetchJson<unknown>(
        "/api/glaci/request-access",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ organizationId: organization.id }),
        },
        { fallbackErrorMessage: "Failed to submit request" },
      );

      setIsSubmitted(true);

      // Update local state to reflect the change
      if (organization) {
        setOrganization({
          ...organization,
          hasRequestedGlaciAccess: true,
        });
      }

      toast.success("Request submitted!", {
        description:
          "Our team will review your request and get back to you soon.",
      });
    } catch (error) {
      showApiErrorToast("Failed to submit request", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Reset state when dialog closes
      setIsSubmitted(false);
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] min-h-[400px] overflow-hidden border-0 bg-card p-0 sm:max-w-xl">
        {/* Close button */}
        <button
          onClick={() => onOpenChange(false)}
          className="absolute right-4 top-4 z-20 rounded-sm opacity-70 transition-opacity hover:opacity-100"
        >
          <X className="h-5 w-5 text-muted-foreground" />
          <span className="sr-only">Close</span>
        </button>

        {/* Background Decorative Elements - Snowfall (covers entire dialog) */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          {/* Large slow particles - background layer */}
          <div
            className="absolute left-[3%] animate-snowflake-slow-1 text-lg text-blue-200/60 dark:text-blue-100/50"
            style={{ animationDelay: "-2s" }}
          >
            •
          </div>
          <div
            className="absolute left-[24%] animate-snowflake-slow-2 text-lg text-blue-200/65 dark:text-blue-100/55"
            style={{ animationDelay: "-18s" }}
          >
            •
          </div>
          <div
            className="absolute left-[48%] animate-snowflake-slow-1 text-lg text-blue-200/60 dark:text-blue-100/50"
            style={{ animationDelay: "-14s" }}
          >
            •
          </div>
          <div
            className="absolute left-[70%] animate-snowflake-slow-3 text-lg text-blue-200/65 dark:text-blue-100/55"
            style={{ animationDelay: "-8s" }}
          >
            •
          </div>
          <div
            className="absolute left-[94%] animate-snowflake-slow-2 text-lg text-blue-200/60 dark:text-blue-100/50"
            style={{ animationDelay: "-20s" }}
          >
            •
          </div>

          {/* Medium particles - main visual layer */}
          <div
            className="absolute left-[2%] animate-snowflake-1 text-base text-blue-200/70 dark:text-blue-100/60"
            style={{ animationDelay: "0s" }}
          >
            •
          </div>
          <div
            className="absolute left-[14%] animate-snowflake-2 text-sm text-blue-300/75 dark:text-blue-200/65"
            style={{ animationDelay: "-3s" }}
          >
            •
          </div>
          <div
            className="absolute left-[26%] animate-snowflake-3 text-base text-blue-200/65 dark:text-blue-100/55"
            style={{ animationDelay: "-7s" }}
          >
            •
          </div>
          <div
            className="absolute left-[38%] animate-snowflake-4 text-sm text-blue-300/70 dark:text-blue-200/60"
            style={{ animationDelay: "-1s" }}
          >
            •
          </div>
          <div
            className="absolute left-[50%] animate-snowflake-5 text-base text-blue-200/75 dark:text-blue-100/65"
            style={{ animationDelay: "-9s" }}
          >
            •
          </div>
          <div
            className="absolute left-[62%] animate-snowflake-6 text-sm text-blue-300/65 dark:text-blue-200/55"
            style={{ animationDelay: "-4s" }}
          >
            •
          </div>
          <div
            className="absolute left-[74%] animate-snowflake-1 text-base text-blue-200/70 dark:text-blue-100/60"
            style={{ animationDelay: "-11s" }}
          >
            •
          </div>
          <div
            className="absolute left-[86%] animate-snowflake-2 text-sm text-blue-300/75 dark:text-blue-200/65"
            style={{ animationDelay: "-2s" }}
          >
            •
          </div>
          <div
            className="absolute left-[98%] animate-snowflake-3 text-base text-blue-200/65 dark:text-blue-100/55"
            style={{ animationDelay: "-6s" }}
          >
            •
          </div>

          {/* Fast small particles - adds movement */}
          <div
            className="absolute left-[5%] animate-snowflake-fast-1 text-xs text-blue-200/60 dark:text-blue-100/50"
            style={{ animationDelay: "0s" }}
          >
            •
          </div>
          <div
            className="absolute left-[15%] animate-snowflake-fast-2 text-[10px] text-blue-300/65 dark:text-blue-200/55"
            style={{ animationDelay: "-1s" }}
          >
            •
          </div>
          <div
            className="absolute left-[25%] animate-snowflake-fast-3 text-xs text-blue-200/55 dark:text-blue-100/45"
            style={{ animationDelay: "-2s" }}
          >
            •
          </div>
          <div
            className="absolute left-[35%] animate-snowflake-fast-4 text-[10px] text-blue-300/60 dark:text-blue-200/50"
            style={{ animationDelay: "-0.5s" }}
          >
            •
          </div>
          <div
            className="absolute left-[45%] animate-snowflake-fast-1 text-xs text-blue-200/65 dark:text-blue-100/55"
            style={{ animationDelay: "-3s" }}
          >
            •
          </div>
          <div
            className="absolute left-[55%] animate-snowflake-fast-2 text-[10px] text-blue-300/55 dark:text-blue-200/45"
            style={{ animationDelay: "-1.5s" }}
          >
            •
          </div>
          <div
            className="absolute left-[65%] animate-snowflake-fast-3 text-xs text-blue-200/60 dark:text-blue-100/50"
            style={{ animationDelay: "-2.5s" }}
          >
            •
          </div>
          <div
            className="absolute left-[75%] animate-snowflake-fast-4 text-[10px] text-blue-300/65 dark:text-blue-200/55"
            style={{ animationDelay: "-0.8s" }}
          >
            •
          </div>
          <div
            className="absolute left-[85%] animate-snowflake-fast-1 text-xs text-blue-200/55 dark:text-blue-100/45"
            style={{ animationDelay: "-3.5s" }}
          >
            •
          </div>
          <div
            className="absolute left-[95%] animate-snowflake-fast-2 text-[10px] text-blue-300/60 dark:text-blue-200/50"
            style={{ animationDelay: "-1.2s" }}
          >
            •
          </div>
        </div>

        {/* Header with logo */}
        <div className="relative px-4 pb-2 pt-8 sm:px-6 sm:pt-10">
          {/* Glaci logo */}
          <div className="relative z-10 mx-auto w-fit">
            <Image
              src="/logos/glaci_dark.svg"
              alt="Glaci"
              width={140}
              height={48}
              className="hidden h-10 dark:block sm:h-12"
              style={{ width: "auto" }}
            />
            <Image
              src="/logos/glaci_light.svg"
              alt="Glaci"
              width={140}
              height={48}
              className="block h-10 dark:hidden sm:h-12"
              style={{ width: "auto" }}
            />
          </div>
        </div>

        <div className="relative z-10 px-4 pb-4 sm:px-6">
          <DialogHeader className="text-center sm:text-center">
            <DialogTitle className="flex items-center justify-center gap-1.5 text-xl font-semibold text-gray-1100 dark:text-gray-100 sm:gap-2 sm:text-2xl">
              <Image
                src="/emojis/seedling.svg"
                alt=""
                width={24}
                height={24}
                className="h-5 w-5 sm:h-6 sm:w-6"
              />
              Join our Early Adopter Program
            </DialogTitle>
            <DialogDescription className="pt-2 text-sm leading-relaxed sm:pt-3 sm:text-base">
              Glaci is currently only available to select organizations in our
              early adopter program. If you think your credit union would be a
              good fit, check out our{" "}
              <Link
                href={PITCH_DECK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-brand-secondary hover:underline"
              >
                pitch deck
              </Link>{" "}
              for next steps.{" "}
              <Image
                src="/emojis/sparkles.svg"
                alt=""
                width={18}
                height={18}
                className="inline h-4 w-4 -translate-y-0.5 sm:h-[18px] sm:w-[18px]"
              />
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 flex flex-col items-center gap-2 sm:mt-6 sm:gap-3">
            {isSubmitted || hasAlreadyRequested ? (
              <Button
                className="w-full bg-emerald-600 text-white hover:bg-emerald-600"
                size="lg"
                disabled
              >
                <CheckCircle className="mr-0.5 h-4 w-4" />
                Request Submitted
              </Button>
            ) : (
              <Button
                className="w-full"
                size="lg"
                onClick={handleRequestAccess}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-0.5 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <span className="truncate">
                    Request Access for {organizationName}
                  </span>
                )}
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={() => handleOpenChange(false)}
              className="w-full text-muted-foreground opacity-70 transition-opacity hover:opacity-100"
            >
              {isSubmitted || hasAlreadyRequested ? "Close" : "Maybe Later"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
